import { useCallback, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import axios from 'axios';
import { normalizeApiError, logError } from '../utils/errorHandler';
import { markSessionExpired, isSessionExpired, SESSION_EXPIRY_REASONS } from '../auth/sessionExpiry';
import { ENV } from '../config/env';

/** Timeout total (ms) para las llamadas normales, que devuelven JSON y responden rápido. */
const DEFAULT_TIMEOUT_MS = 10000;

/**
 * Silencio máximo tolerado (ms) en una descarga antes de darla por cortada.
 *
 * <p>No es un tope de duración: se reinicia con cada bloque de bytes que llega. Una descarga sana de
 * 200 MB por un enlace lento nunca lo alcanza; una que se quedó colgada, sí.
 */
const DOWNLOAD_STALL_MS = 45000;

/** Una descarga binaria: su duración depende del tamaño del archivo y de la velocidad del enlace. */
const isBinaryDownload = (config) =>
  config?.responseType === 'blob' || config?.responseType === 'arraybuffer';

/**
 * ¿El access token guardado ya venció?
 *
 * <p>Es la única evidencia local de que un 401 es por sesión y no por permisos. Devuelve false
 * si no hay usuario o si no hay `expires_at`: ante la duda no se afirma que la sesión venció.
 */
const tokenVencido = (auth) => {
  const expiraEn = auth?.user?.expires_at;
  return typeof expiraEn === 'number' && expiraEn * 1000 <= Date.now();
};

/**
 * ¿El servidor dijo que el token no sirve?
 *
 * <p>Un 401 por token vencido o invalido trae
 * {@code WWW-Authenticate: Bearer error="invalid_token"}; uno por falta de rol NO trae el header,
 * porque pasa por {@code CustomAccessDeniedHandler}, que solo setea el status (#58).
 *
 * <p><b>Esta es la señal que manda</b>, y no el reloj del navegador: quien decide si un token
 * vencio es el servidor. En el incidente del 2026-09-08 el cliente creia que le quedaban minutos
 * de token mientras la API respondia `Jwt expired`, asi que la sesion nunca se marcaba, el aviso
 * nunca aparecia y nada frenaba las requests: seis horas y media, 23.928 en diez minutos (#179).
 */
const servidorRechazoElToken = (error) => {
  const headers = error?.response?.headers;
  const h = headers?.['www-authenticate'] ?? headers?.get?.('www-authenticate');
  return typeof h === 'string' && h.includes('invalid_token');
};

/**
 * Hook base para hacer llamadas a la API
 * No muestra notificaciones automáticamente - los errores deben manejarse manualmente
 */
export const useApiClient = () => {
  const auth = useAuth();
  // El 401 se juzga contra el token VIGENTE, no contra el que llevaba la request. `apiCall` esta
  // memoizado, asi que una request en vuelo cierra sobre el `auth` anterior: sin este ref, un 401
  // tardio de una request lanzada antes de renovar marcaba la sesion DESPUES de que la renovacion
  // entro bien, y dejaba un aviso falso sobre una sesion sana que ya nada limpiaba (#172).
  const authRef = useRef(auth);
  authRef.current = auth;

  const apiCall = useCallback(async (config) => {
    // Con la sesion ya marcada no se sale a la red: el token no sirve y la respuesta va a ser 401.
    // Marcar sin bloquear dejaba que requests en vuelo y efectos de componentes montados detras
    // del aviso siguieran mandando el Bearer muerto (#172).
    if (isSessionExpired()) {
      const err = new Error('La sesión expiró.');
      err.code = 'ERR_SESSION_EXPIRED';
      throw err;
    }
    const token = auth?.user?.access_token;

    // Las descargas binarias NO llevan tope total (GitLab #40). El de 10s abortaba la descarga de
    // la versión a firmar —2,5 MB, que por VPN tardan más que eso— y el firmante veía "No se pudo
    // preparar la firma del documento" con el servidor entregando el archivo sin ningún problema.
    // El mismo tope pesaba sobre el visor de PDF, la descarga del buscador, la exportación de
    // series y el instalador del cliente de firma, que pesa decenas de MB y nunca habría entrado
    // en 10 segundos.
    //
    // En su lugar va un detector de silencio: se aborta si dejan de llegar bytes, no si la
    // descarga es larga. Un tope fijo siempre castiga al archivo grande o al enlace lento; lo que
    // hay que detectar es la transferencia muerta.
    const download = isBinaryDownload(config);
    const controller = download && typeof AbortController !== 'undefined' ? new AbortController() : null;
    let stallTimer = null;
    let stalled = false;

    const armStallTimer = () => {
      if (!controller) return;
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => { stalled = true; controller.abort(); }, DOWNLOAD_STALL_MS);
    };

    const axiosConfig = {
      baseURL: ENV.API_URL,
      timeout: download ? 0 : DEFAULT_TIMEOUT_MS,
      ...config,
      ...(controller && {
        signal: controller.signal,
        onDownloadProgress: (event) => {
          armStallTimer();
          config.onDownloadProgress?.(event);
        },
      }),
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...config.headers,
      },
    };

    try {
      armStallTimer();
      const response = await axios(axiosConfig);
      return response;
    } catch (error) {
      if (stalled) {
        // Distinguible del abort deliberado de un caller: la descarga se cortó sola.
        const stallError = new Error(
          `La descarga se interrumpió: no llegaron datos durante ${DOWNLOAD_STALL_MS / 1000} segundos.`
        );
        stallError.code = 'ERR_DOWNLOAD_STALLED';
        logError(stallError, config.url || 'API call');
        throw stallError;
      }
      // Normalizar el error para logging
      const normalizedError = normalizeApiError(error);
      const statusCode = normalizedError.statusCode;
      
      // Log estructurado del error
      logError(error, config.url || 'API call');
      
      // Un 401 con el token YA VENCIDO significa que la sesión dejó de servir. Acá sólo se deja
      // constancia; quién avisa al usuario lo decide AutoLogin, único dueño de esa presentación.
      //
      // No alcanza con que el status sea 401, y esto es lo importante: en esta API un usuario
      // perfectamente autenticado pero sin el rol necesario TAMBIEN recibe 401, porque
      // CustomAccessDeniedHandler setea UNAUTHORIZED en vez de FORBIDDEN (backlog #58, todavía
      // pendiente). Marcar por status desnudo bloquearía con un aviso de sesión vencida a alguien
      // cuya sesión está viva — y volver a loguearse no le devolvería el rol que le falta. Pasa,
      // por ejemplo, con un usuario sin rol SIGNER en `signatureClientService.getManifest()`, que
      // hoy traga el 401 y no muestra el widget.
      //
      // Por eso el discriminante es el vencimiento real del token, que es un dato local y gratis.
      // Un 401 con el token vigente se deja pasar tal cual al caller, como hasta ahora.
      //
      // Y se mira `expires_at` y no `auth.isAuthenticated`: al vencer el token sin renovación,
      // react-oidc-context no despacha nada y deja ese flag congelado en su último valor.
      // Apoyarse en él sería apoyarse justo en lo que está roto.
      // El header manda; el reloj local queda como respaldo para cuando no se puede leer —por
      // ejemplo si la API y la UI viven en origenes distintos y falta exponerlo.
      if (statusCode === 401 && (servidorRechazoElToken(error) || tokenVencido(authRef.current))) {
        markSessionExpired(SESSION_EXPIRY_REASONS.API_401);
      }
      
      // Re-lanzar el error para que el componente pueda manejarlo
      throw error;
    } finally {
      // Siempre: un timer vivo tras una descarga exitosa abortaría la SIGUIENTE request del mismo
      // controller y, mientras tanto, mantendría el callback en memoria.
      clearTimeout(stallTimer);
    }
  }, [auth?.user?.access_token]);

  /**
   * Descarga un binario grande con {@code fetch}, no con axios.
   *
   * <p>El instalador del cliente de firma pesa ~90 MB y por axios/XHR se cortaba de forma
   * consistente a los ~500 ms —apenas 10-15 MB— con un fallo de red sin código HTTP, mientras que un
   * {@code fetch} plano contra el MISMO endpoint bajaba los 90 MB completos en ~4 s. Diagnosticado
   * en el navegador contra el entorno de test: tres descargas por fetch, las tres 200 con el binario
   * entero.
   *
   * <p>No cambiamos el camino de axios para el resto: los documentos son de pocos MB y ahí funciona.
   * Ésta es la excepción por tamaño, y va por fetch porque es el que está demostrado que aguanta.
   *
   * @returns {Promise<Blob>} el binario descargado
   */
  const downloadBinary = useCallback(async (url) => {
    if (isSessionExpired()) {
      const err = new Error('La sesión expiró.');
      err.code = 'ERR_SESSION_EXPIRED';
      throw err;
    }
    const token = auth?.user?.access_token;
    // API_URL termina en barra en los entornos desplegados y las rutas empiezan con barra:
    // concatenar crudo produce "//api/v1/...". Axios lo normaliza por su cuenta (combineURLs);
    // fetch no, así que hay que hacerlo acá.
    const base = (ENV.API_URL || '').replace(/\/+$/, '');
    const path = url.startsWith('/') ? url : `/${url}`;
    const res = await fetch(`${base}${path}`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // Este camino va por fetch y no pasa por el manejo de errores de apiCall, así que el 401
      // hay que marcarlo acá también: si no, una descarga es el único modo de encontrarse con
      // la sesión muerta y que nadie se entere (#168). Mismo criterio de vencimiento real.
      if (res.status === 401 && tokenVencido(authRef.current)) {
        markSessionExpired(SESSION_EXPIRY_REASONS.API_401);
      }
      const err = new Error(`La descarga falló con estado ${res.status}.`);
      err.status = res.status;
      err.response = { status: res.status };
      logError(err, url);
      throw err;
    }
    return res.blob();
  }, [auth?.user?.access_token]);

  return {
    downloadBinary,
    get: (url, config = {}) => apiCall({ ...config, method: 'GET', url }),
    post: (url, data, config = {}) => apiCall({ ...config, method: 'POST', url, data }),
    put: (url, data, config = {}) => apiCall({ ...config, method: 'PUT', url, data }),
    delete: (url, config = {}) => apiCall({ ...config, method: 'DELETE', url }),
  };
};
